'use client'

import React from 'react'
import Link from 'next/link'
import Image from 'next/image'
import { motion } from 'framer-motion'
import { Box, Text, Title } from '@mantine/core'
import { ServiceItem } from '@/types/interfaces/service-item'
import { PageLocation, StoreAnalytics, trackStoreEvent } from '@nile/mantine-theme/analytics'
import { cardFadeInVariants, cardContentFadeInVariants } from '@/lib/animations'
import { getServiceHref } from '@/lib/getServiceHref'

import ContravaeImg from '@/assets/images/refresh/categories/weight-loss/product-tiles/contrave.webp'
import FoundayoImg from '@/assets/images/refresh/categories/weight-loss/product-tiles/foundayo.webp'
import InjectWegovyImg from '@/assets/images/refresh/categories/weight-loss/product-tiles/injectable-wegovy.webp'
import MounjaroImg from '@/assets/images/refresh/categories/weight-loss/product-tiles/mounjaro.webp'
import OralWebovyImg from '@/assets/images/refresh/categories/weight-loss/product-tiles/oral-wegovy.webp'
import OzempicImg from '@/assets/images/refresh/categories/weight-loss/product-tiles/ozempic.webp'
import SaxendaImg from '@/assets/images/refresh/categories/weight-loss/product-tiles/saxenda.webp'
import ZepboundImg from '@/assets/images/refresh/categories/weight-loss/product-tiles/zepbound.webp'

interface ProductImageCardProps {
    product: ServiceItem
    index?: number
    pageLocation: PageLocation
    additionalInformation?: Record<string, unknown>
}

const CUSTOM_IMAGE_MAP = {
    201448: ZepboundImg,
    202690: FoundayoImg,
    202682: OralWebovyImg,
    202184: InjectWegovyImg,
    201446: MounjaroImg,
    202198: OzempicImg,
    201992: SaxendaImg,
    201668: ContravaeImg,
}

const CUSTOM_DESCRIPTION_MAP: Record<string, string> = {
    '201448': 'Dual-action GLP-1/GIP treatment for chronic weight management',
    '202690': 'Daily GLP-1 pill for chronic weight management',
    '202682': 'Daily pill form of Wegovy for lasting weight support',
    '202184': 'Once-weekly GLP-1 approved for chronic weight management',
    '201446': 'Dual-action GLP-1/GIP treatment for those with qualifying needs',
    '202198': 'Once-weekly GLP-1 for those with diabetes',
    '201992': 'Daily GLP-1 for chronic weight management',
    '201668': 'Daily non-GLP-1 support for appetite and cravings',
}

export default function ProductImageCard({
    product,
    index = 0,
    pageLocation,
    additionalInformation,
}: ProductImageCardProps) {
    const href = getServiceHref(product)
    const customImage = (CUSTOM_IMAGE_MAP as Record<string, typeof ZepboundImg>)[product.id]
    const imageUrl =
        customImage ||
        (product.imageUrl ? `https://${process.env.NEXT_PUBLIC_ASSETS_DOMAIN}/${product.imageUrl}` : null)

    const cardContent = (
        <Box
            component={motion.div}
            initial="hidden"
            animate="visible"
            variants={cardFadeInVariants}
            pos="relative"
            style={{
                width: 'clamp(240px, calc(283 * var(--vw-unit)), 283px)',
                height: 'clamp(240px, calc(283 * var(--vw-unit)), 283px)',
                padding: 'clamp(20px, calc(24 * var(--vw-unit)), 24px)',
                backgroundColor: 'rgba(255, 255, 255, 0.95)',
                borderRadius: 'clamp(12px, calc(16 * var(--vw-unit)), 16px)',
                cursor: href ? 'pointer' : 'default',
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                gap: 'clamp(12px, calc(16 * var(--vw-unit)), 16px)',
            }}
        >
            <Box
                component={motion.div}
                initial="hidden"
                animate="visible"
                variants={cardContentFadeInVariants(index * 0.05 + 0.1)}
                style={{ position: 'relative', zIndex: 1 }}
            >
                <Title
                    order={3}
                    fz={24}
                    fw="var(--weight-display-small, 450)"
                    lh="110%"
                    lts="-0.279px"
                    c="gray.9"
                    m={0}
                    style={{ fontStyle: 'normal' }}
                >
                    {product.title}
                </Title>
            </Box>

            {product.description && (
                <Box
                    component={motion.div}
                    initial="hidden"
                    animate="visible"
                    variants={cardContentFadeInVariants(index * 0.05 + 0.15)}
                    style={{ position: 'relative', zIndex: 1 }}
                >
                    <Text
                        fz={14}
                        fw="var(--weight-text, 450)"
                        lh="130%"
                        c="gray.6"
                        m={0}
                        style={{ fontStyle: 'normal' }}
                    >
                        {CUSTOM_DESCRIPTION_MAP[product.id?.toString() ?? ''] || product.description}
                    </Text>
                </Box>
            )}

            {imageUrl && (
                <Box
                    component={motion.div}
                    initial="hidden"
                    animate="visible"
                    variants={cardContentFadeInVariants(index * 0.05 + 0.2)}
                    pos="absolute"
                    top={0}
                    right={0}
                    bottom={0}
                    left={0}
                    style={{
                        zIndex: 0,
                        width: '100%',
                        height: '100%',
                    }}
                >
                    <Image
                        src={imageUrl}
                        alt={product.title}
                        fill
                        sizes="283px"
                        draggable={false}
                        style={{
                            objectFit: 'contain',
                            objectPosition: 'bottom right',
                        }}
                    />
                </Box>
            )}
        </Box>
    )

    if (href) {
        return (
            <Link
                href={href}
                style={{ textDecoration: 'none', color: 'inherit' }}
                draggable={false}
                onClick={() => {
                    trackStoreEvent(StoreAnalytics.CLICKED_SKU, {
                        location: pageLocation,
                        additionalInformation,
                        sku: product,
                    })
                }}
            >
                {cardContent}
            </Link>
        )
    }

    return cardContent
}
